import { useTheme } from '@material-ui/core';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import { Box, Typography } from '@material-ui/core';

import { ChartDataPoint } from '@vyce/core/src/types';

export const FormSummaryBarChart = ({
  data,
  layout,
  onItemClick,
}: {
  data: ChartDataPoint[];
  layout: 'horizontal' | 'vertical';
  onItemClick?: (answerValue: string, answerLabel: string) => void;
}) => {
  const theme = useTheme();

  const chartData: BarDatum[] = data.map(item => ({
    id: item.label,
    value: item.count,
  }));

  const maxLabelLength = Math.max(...data.map(item => item.label.length));
  const leftMargin = Math.min(Math.max(maxLabelLength * 7, 60), 150);

  const marginOptions = { top: 20, right: 120, bottom: 40, left: leftMargin };

  if (chartData.length === 0) {
    return null;
  }

  return (
    <Box
      style={{
        height: 300,
        width: '100%',
        marginTop: 16,
        marginLeft: 0,
        paddingLeft: 0,
      }}
    >
      <ResponsiveBar
        data={chartData}
        keys={['value']}
        indexBy="id"
        onClick={
          onItemClick
            ? datum => {
                const point = data.find(item => item.label === datum.indexValue);
                if (point) onItemClick(point.value, point.label);
              }
            : undefined
        }
        margin={marginOptions}
        padding={0.3}
        layout={layout}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={theme.palette.primary.main}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          format: value => `${value}`,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
        }}
        enableGridX={true}
        enableGridY={false}
        label={d => `${d.value}`}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={theme.palette.type === 'light' ? '#fff' : '#000'}
        enableLabel={true}
        theme={{
          axis: {
            ticks: {
              line: {
                stroke: theme.palette.divider,
              },
              text: {
                fill: theme.palette.text.secondary,
                fontSize: 12,
                fontWeight: 600,
              },
            },
            legend: {
              text: {
                fill: theme.palette.primary.main,
                fontSize: 12,
              },
            },
          },
          grid: {
            line: {
              stroke: theme.palette.divider,
              strokeDasharray: '2 2',
              strokeWidth: 1,
            },
          },
        }}
        tooltip={({ id, value, data: tooltipData }) => {
          return (
            <Box
              style={{
                padding: '8px 12px',
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
                boxShadow: theme.shadows[2],
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {id}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {value}
              </Typography>
            </Box>
          );
        }}
        animate={false}
      />
    </Box>
  );
};
